import React, { useState } from 'react';
import { 
  User, 
  Bell, 
  Shield, 
  Palette, 
  Moon, 
  Sun,
  Smartphone,
  Mail,
  Clock,
  Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Settings.css';

const Settings = () => {
  const [settings, setSettings] = useState({
    notifications: {
      medication: true,
      appointments: true,
      healthReminders: true,
      email: false,
      push: true
    },
    appearance: {
      theme: 'light',
      fontSize: 'medium'
    },
    privacy: {
      shareData: false,
      analytics: true
    },
    reminders: {
      medicationTime: 15,
      appointmentTime: 60,
      healthCheck: 24
    }
  });

  const [userProfile, setUserProfile] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    dateOfBirth: '1990-01-01',
    emergencyContact: {
      name: 'Jane Doe',
      phone: '+1 (555) 987-6543',
      relationship: 'Spouse'
    }
  });

  const handleSettingChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const handleProfileChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  const saveSettings = () => {
    // In a real app, this would save to backend/localStorage
    toast.success('Settings saved successfully!');
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Customize your app preferences and profile</p>
      </div>

      <div className="settings-container">
        <div className="settings-grid">
          {/* Profile Section */}
          <div className="settings-section">
            <div className="section-header">
              <User size={24} />
              <h2>Profile Information</h2>
            </div>
            
            <div className="profile-form">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={userProfile.name}
                    onChange={(e) => handleProfileChange('name', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={userProfile.email}
                    onChange={(e) => handleProfileChange('email', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={userProfile.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    className="form-input"
                    value={userProfile.dateOfBirth}
                    onChange={(e) => handleProfileChange('dateOfBirth', e.target.value)}
                  />
                </div>
              </div>

              <div className="emergency-contact">
                <h3>Emergency Contact</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={userProfile.emergencyContact.name}
                      onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={userProfile.emergencyContact.phone}
                      onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Relationship</label>
                    <input
                      type="text"
                      className="form-input"
                      value={userProfile.emergencyContact.relationship}
                      onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="settings-section">
            <div className="section-header">
              <Bell size={24} />
              <h2>Notifications</h2>
            </div>
            
            <div className="settings-options">
              <div className="setting-item">
                <div className="setting-info">
                  <h3>Medication Reminders</h3>
                  <p>Get notified when it's time to take your medication</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.medication}
                    onChange={(e) => handleSettingChange('notifications', 'medication', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Appointment Reminders</h3>
                  <p>Receive notifications before your medical appointments</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.appointments}
                    onChange={(e) => handleSettingChange('notifications', 'appointments', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Health Check Reminders</h3>
                  <p>Regular reminders to log your health data</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.healthReminders}
                    onChange={(e) => handleSettingChange('notifications', 'healthReminders', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Email Notifications</h3>
                  <p>Receive notifications via email</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.email}
                    onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Push Notifications</h3>
                  <p>Receive notifications on your device</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.push}
                    onChange={(e) => handleSettingChange('notifications', 'push', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Appearance Section */}
          <div className="settings-section">
            <div className="section-header">
              <Palette size={24} />
              <h2>Appearance</h2>
            </div>
            
            <div className="settings-options">
              <div className="setting-item">
                <div className="setting-info">
                  <h3>Theme</h3>
                  <p>Choose your preferred theme</p>
                </div>
                <div className="theme-selector">
                  <button
                    className={`theme-btn ${settings.appearance.theme === 'light' ? 'active' : ''}`}
                    onClick={() => handleSettingChange('appearance', 'theme', 'light')}
                  >
                    <Sun size={16} />
                    Light
                  </button>
                  <button
                    className={`theme-btn ${settings.appearance.theme === 'dark' ? 'active' : ''}`}
                    onClick={() => handleSettingChange('appearance', 'theme', 'dark')}
                  >
                    <Moon size={16} />
                    Dark
                  </button>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Font Size</h3>
                  <p>Adjust the text size for better readability</p>
                </div>
                <select
                  className="form-input"
                  value={settings.appearance.fontSize}
                  onChange={(e) => handleSettingChange('appearance', 'fontSize', e.target.value)}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>
          </div>

          {/* Privacy Section */}
          <div className="settings-section">
            <div className="section-header">
              <Shield size={24} />
              <h2>Privacy & Security</h2>
            </div>
            
            <div className="settings-options">
              <div className="setting-item">
                <div className="setting-info">
                  <h3>Share Health Data</h3>
                  <p>Allow sharing anonymized health data for research</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.privacy.shareData}
                    onChange={(e) => handleSettingChange('privacy', 'shareData', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Analytics</h3>
                  <p>Help improve the app by sharing usage analytics</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.privacy.analytics}
                    onChange={(e) => handleSettingChange('privacy', 'analytics', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Reminder Timing Section */}
          <div className="settings-section">
            <div className="section-header">
              <Clock size={24} />
              <h2>Reminder Timing</h2>
            </div>
            
            <div className="settings-options">
              <div className="setting-item">
                <div className="setting-info">
                  <h3>Medication Reminder (minutes before)</h3>
                  <p>How early to remind you about medication</p>
                </div>
                <input
                  type="number"
                  className="form-input"
                  value={settings.reminders.medicationTime}
                  onChange={(e) => handleSettingChange('reminders', 'medicationTime', parseInt(e.target.value))}
                  min="1"
                  max="60"
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Appointment Reminder (minutes before)</h3>
                  <p>How early to remind you about appointments</p>
                </div>
                <input
                  type="number"
                  className="form-input"
                  value={settings.reminders.appointmentTime}
                  onChange={(e) => handleSettingChange('reminders', 'appointmentTime', parseInt(e.target.value))}
                  min="15"
                  max="1440"
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3>Health Check Reminder (hours)</h3>
                  <p>How often to remind you to log health data</p>
                </div>
                <input
                  type="number"
                  className="form-input"
                  value={settings.reminders.healthCheck}
                  onChange={(e) => handleSettingChange('reminders', 'healthCheck', parseInt(e.target.value))}
                  min="1"
                  max="168"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button className="btn btn-primary" onClick={saveSettings}>
            <Save size={16} />
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings; 